/** Standard amortized monthly principal & interest. Zero-rate degrades to simple division. */
export function monthlyPI(principal: number, annualRatePct: number, years: number): number {
  if (principal <= 0 || years <= 0) return 0;
  const n = years * 12;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return principal / n;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

export const CA_TAX_RATE = 0.0118; // approximate CA effective property tax
export const CA_INSURANCE_RATE = 0.004; // ~0.4%/yr homeowner's insurance estimate

export interface PaymentBreakdown {
  pi: number;
  tax: number;
  insurance: number;
  hoa: number;
  total: number;
}

/**
 * Full PITI (principal, interest, taxes, insurance) + HOA. Insurance is part of
 * the true monthly cost — omitting it understates the payment and inflates
 * affordability — so every monthly-cost surface shares this one model.
 */
export function paymentBreakdown(
  price: number,
  downPct: number,
  annualRatePct: number,
  years: number,
  hoaFee = 0,
  insuranceRatePct = CA_INSURANCE_RATE * 100
): PaymentBreakdown {
  const principal = price * (1 - downPct / 100);
  const pi = monthlyPI(principal, annualRatePct, years);
  const tax = (price * CA_TAX_RATE) / 12;
  const insurance = (price * (insuranceRatePct / 100)) / 12;
  return { pi, tax, insurance, hoa: hoaFee, total: Math.round(pi + tax + insurance + hoaFee) };
}
